import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Onboarding } from '@ofself/shell';
import AccountFrame, { AppLine } from '../components/AccountFrame';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common';
import { isSafeRedirectUrl } from '../utils/redirectSafety';
import { resolveNext } from '../utils/loginRedirect';
import {
  withNext, appForNext, isPhoneVerificationRequired, phoneStepState, holdPassword,
} from '../utils/joinFlow';

function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The ofSelf app this sign-in returns to, from the shell's registry (joinFlow).
  const app = appForNext(resolveNext(searchParams.get('next') || searchParams.get('returnUrl')));

  // Show expired message as toast, then clear from URL
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.info('Your session has expired. Please log in again.');
      // Remove 'expired' from URL without triggering navigation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('expired');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(identifier, password);

      toast.success('Logged in successfully!');

      // Check if there's a return URL (for authorization flow)
      // Support both 'next' (OAuth standard) and 'returnUrl' (legacy)
      //
      // resolveNext, NOT decodeURIComponent: URLSearchParams has already
      // decoded once, and decoding a consent URL a second time unescapes the
      // `redirect_uri` nested inside it — the app's callback, truncated at its
      // first `&`. Legacy double-encoded values still decode, see the util.
      const nextUrl = resolveNext(searchParams.get('next') || searchParams.get('returnUrl'));
      if (nextUrl && isSafeRedirectUrl(nextUrl)) {
        // If it's a full URL (third-party authorization), redirect there
        if (nextUrl.startsWith('http://') || nextUrl.startsWith('https://')) {
          window.location.href = nextUrl;
        } else {
          navigate(nextUrl);
        }
      } else {
        navigate('/graph');
      }
    } catch (err) {
      // An account whose signup stopped before its phone was proved: the
      // password is right, so finish the join there (pages/join/JoinPhone).
      if (isPhoneVerificationRequired(err)) {
        holdPassword(password);
        const nextParam = resolveNext(searchParams.get('next') || searchParams.get('returnUrl'));
        navigate(withNext('/join/phone', isSafeRedirectUrl(nextParam) ? nextParam : null), {
          state: phoneStepState(err),
        });
        return;
      }
      const message = err.message || 'Login failed';
      // Show specific messages for common errors
      if (err.code === 'RATE_LIMIT_EXCEEDED' || err.status === 429) {
        // Per-account lockout after repeated wrong passwords (or too many tries from here).
        toast.error('Too many sign-in attempts. Please wait a while and try again.');
      } else if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('credentials') || message.toLowerCase().includes('password')) {
        toast.error('Invalid email/username or password. Please try again.');
      } else if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('no user') || message.toLowerCase().includes('no account')) {
        toast.error('No account found. Please check or register.');
      } else if (message.toLowerCase().includes('network')) {
        toast.error('Unable to connect to server. Please check your connection.');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // The account frame (components/AccountFrame): the shell's <Onboarding>
  // surface, as joining is. The app owns state, validation and routing.
  const nextParam = resolveNext(searchParams.get('next') || searchParams.get('returnUrl'));
  const go = (to) => (e) => { e.preventDefault(); navigate(to); };
  return (
    <AccountFrame>
      <Onboarding.Step
        // the fields say what the page is; the title is for a reader alone
        title={app ? `Sign in to continue to ${app.name}` : 'Sign in to ofSelf'}
        titleHidden
        note={(
          <span className="account-links">
            <a href="/forgot-password" onClick={go('/forgot-password')}>Forgot password?</a>
            <a href={withNext('/register', nextParam)} onClick={go(withNext('/register', nextParam))}>New to ofSelf? Create an account</a>
          </span>
        )}
        centered
      >
        <AppLine app={app}>Sign in with ofSelf to continue to {app?.name}</AppLine>
        <form className="account-form" onSubmit={handleSubmit}>
          <Onboarding.Fields>
            <Onboarding.Field
              label="email or username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <Onboarding.Field
              label="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Onboarding.Fields>
          <Onboarding.Next type="submit" disabled={loading} go={!loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Onboarding.Next>
        </form>
      </Onboarding.Step>
    </AccountFrame>
  );
}

export default Login;
